import store from "@/store";
import { CouponData, CouponItem } from "@/model/Coupon";
import { InspectDetail } from "@/model/InspectDetail";

const namespace = "InspectionDetail";

export class InspectionCouponDetailStoreService {
  private couponList: CouponItem[] = [];
  deSelCoupon: boolean = false;

  // サービス初期化時にクーポンの選択管理枠をStoreへ登録する
  constructor() {
    this.addNowSelected();
  }

  // クーポンが選択済みかどうかを参照する
  get nowSelected(): boolean {
    const nowSelected = store.getters[`${namespace}/getNowSelected`];
    if (nowSelected.coupon === true || nowSelected.coupon === false) {
      return nowSelected.coupon;
    }
    this.addNowSelected();
    return false;
  }

  // クーポンの選択管理枠をStoreへ追加する
  addNowSelected() {
    return store.dispatch(`${namespace}/addNowSelected`, "coupon");
  }

  // クーポンの選択済み状態をStoreへ反映する
  setNowSelected(payload: boolean) {
    return store.dispatch(`${namespace}/setNowSelected`, {
      key: "coupon",
      value: payload
    });
  }

  // 共通処理から扱うクーポン一覧を参照する
  get list(): CouponItem[] {
    return this.couponList;
  }

  // 共通処理からクーポン一覧を保持する
  setList(payload: CouponItem[]) {
    this.couponList = payload;
  }

  // クーポンの選択状態からスタート可否判定用の状態を更新する
  checkDisableStart(arrData: CouponItem[], isUnSelect?: boolean) {
    let checked: boolean = false;
    if (isUnSelect) {
      checked = true;
    } else if (arrData) {
      checked = arrData.some((data: CouponItem) => data.isCheck === 1);
    }
    this.setNowSelected(checked);
  }

  // テンプレート表示用のクーポン選択肢リストを生成する
  getTargetList() {
    return this.list.map((data: CouponItem) => {
      const key = data.couponId || data.num;
      return {
        "mode": 3,
        "btnDel": false,
        "btnSel": data.isCheck === 1,
        "btnFre": !(data.isCheck === 1),
        "btnUse": false,
        "key": key,
        "dataSet": key,
        "isDisabled": false,
        "icon": "",
        "label": data.couponNm,
        "labelEntends1": data.couponZn,
        "labelEntends2": data.couponZk,
      };
    });
  }

  // スタート時に選択中のクーポンを保存データへ反映する
  setStartInspectDetail(inspectionDetail: InspectDetail) {
    inspectionDetail.coupon = this.list
      .filter((data: CouponItem) => data.isCheck === 1)
      .map((data: CouponItem) => {
        return {
          num: data.num,
          couponId: data.couponId,
          couponNm: data.couponNm,
          couponZn: data.couponZn,
          couponZk: data.couponZk,
          renban: data.renban
        };
      });
  }

  // 取得済みの検査構成からクーポンの初期選択情報を反映する
  applyInitialInspectionDetail(data: any, inspectionDetail?: InspectDetail) {
    if (inspectionDetail) {
      inspectionDetail.coupon = data.coupon || [];
    }
    return { data, inspectionDetail };
  }

  // クーポンの選択クリックを処理する
  select(key: number) {
    const couponList = this.list.map((data: CouponItem) => {
      const dataKey = data.couponId || data.num;
      if (dataKey == key) {
        const isChecked = data.isCheck === 1;
        data.isCheck = isChecked ? 0 : 1;
        if (!isChecked) {
          this.deSelCoupon = false;
        }
      }
      return data;
    });
    this.setList(couponList);
    this.checkDisableStart(couponList, this.deSelCoupon);
  }

  // クーポンで「選択無し」を表示できるか返す
  canDeSelect() {
    return true;
  }

  // クーポンが「選択無し」状態か返す
  isDeSelected() {
    return this.deSelCoupon;
  }

  // クーポンの「選択無し」状態を切り替える
  deSelect() {
    const couponList = this.list;
    if (!this.deSelCoupon) {
      couponList.map(data => (data.isCheck = 0));
    }
    this.deSelCoupon = !this.deSelCoupon;
    this.setList([...couponList]);
    this.checkDisableStart(couponList, this.deSelCoupon);
    return this.deSelCoupon;
  }

  // 共通呼び出し用の「選択無し」クリック処理
  clickDeSelect() {
    return this.deSelect();
  }

  // Storeに既存リストがある場合に選択状態を再評価する
  applyExistingList() {
    const couponList = this.list;
    this.checkDisableStart(couponList, this.deSelCoupon);
    return couponList;
  }

  // API取得結果からクーポン一覧の初期選択状態を作成する
  setInitialList(res: CouponData, selectedCoupon: CouponItem[] | undefined, initialDeSelected?: boolean) {
    const responseItems = res && res.coupon && res.coupon.items ? res.coupon.items : [];
    const couponList = responseItems.map((data: CouponItem) => {
      const selected = selectedCoupon
        ? selectedCoupon.some((coupon: CouponItem) =>
          data.couponId == coupon.couponId
          && (data.couponId !== 0 || data.renban === coupon.renban)
        )
        : false;
      data.isCheck = selected || data.isCheck === 1 ? 1 : 0;
      return data;
    });
    this.deSelCoupon = initialDeSelected === true
      && !couponList.some((data: CouponItem) => data.isCheck === 1);
    this.setList(couponList);
    this.checkDisableStart(couponList, this.deSelCoupon);
    return couponList;
  }

  // クーポン一覧を既存保持データまたはAPIから取得する
  getDataList(
    loadInitialList: () => Promise<CouponData>,
    selectedCoupon: CouponItem[] | undefined,
    initialDeSelected?: boolean
  ) {
    if (this.list.length > 0) {
      this.applyExistingList();
      return {
        hasExistingList: true,
        promise: null
      };
    }
    return {
      hasExistingList: false,
      promise: loadInitialList().then((res: CouponData) => {
        return this.setInitialList(res, selectedCoupon, initialDeSelected);
      })
    };
  }
}
